import { useCallback, useEffect, useState } from "react";
import { api, apiError, inr } from "@/lib/api";
import { toast } from "sonner";
import { Download, Wallet, CreditCard, Plus, Trash2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const now = new Date();
const START = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
const FYS = Array.from({ length: 6 }, (_, i) => `${START - i}-${String((START - i + 1) % 100).padStart(2, "0")}`);
const HEADS = [["igst", "IGST"], ["cgst", "CGST"], ["sgst", "SGST"], ["cess", "Cess"]];

function Balance({ title, icon: Icon, bal, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 small-label"><Icon className="h-4 w-4 text-indigo-400" /> {title}</div>
      <div className="grid grid-cols-4 gap-2 mt-3 font-mono text-sm">
        {HEADS.map(([h, l]) => <div key={h}><div className="text-[10px] text-muted-foreground">{l}</div><div>{inr(bal[h])}</div></div>)}
      </div>
    </div>
  );
}

function LedgerTable({ title, rows, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-border font-head font-semibold text-sm">{title}</div>
      <table className="w-full text-[11px] font-mono">
        <thead className="text-muted-foreground">
          <tr className="[&>th]:px-2 [&>th]:py-2 [&>th]:text-left border-b border-border">
            <th>Date</th><th>Period</th><th>Reference</th><th>Description</th><th>Type</th>
            {HEADS.map(([, l]) => <th key={l} className="text-right">{l}</th>)}
            {HEADS.map(([, l]) => <th key={`b${l}`} className="text-right">Bal. {l}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border/40 [&>td]:px-2 [&>td]:py-1.5">
              <td className="whitespace-nowrap">{r.date ? r.date.split("-").reverse().join("-") : ""}</td><td>{r.period}</td><td className="whitespace-nowrap">{r.ref}</td>
              <td className="font-sans min-w-[220px]">{r.description}</td>
              <td className={r.type === "Debit" ? "text-rose-400" : r.type === "Opening" ? "text-muted-foreground" : r.assumed ? "text-amber-400" : "text-emerald-400"}>{r.type}{r.assumed ? "*" : ""}</td>
              {HEADS.map(([h]) => <td key={h} className="text-right">{inr(r.amount[h])}</td>)}
              {HEADS.map(([h]) => <td key={`b${h}`} className="text-right text-muted-foreground">{inr(r.balance[h])}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={13} className="px-2 py-4 text-center text-muted-foreground font-sans">No filed GSTR-3B returns in this year</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

const EMPTY = { cpin: "", cin: "", deposit_date: "", period: "", igst: "", cgst: "", sgst: "", cess: "", remarks: "" };
const FIELDS = [["cpin", "CPIN (14 digits)"], ["cin", "CIN (optional)"], ["deposit_date", "Deposit date (DD-MM-YYYY)"], ["period", "Tax period MMYYYY (optional)"],
  ["igst", "IGST ₹"], ["cgst", "CGST ₹"], ["sgst", "SGST ₹"], ["cess", "Cess ₹"]];

function ChallansCard({ clientId, onChange }) {
  const { user } = useAuth();
  const canEdit = user?.role !== "reader";
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/clients/${clientId}/challans`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    setBusy(true);
    try {
      const body = { ...form };
      ["igst", "cgst", "sgst", "cess"].forEach((h) => { body[h] = Number(form[h] || 0); });
      const { data: saved } = await api.post(`/clients/${clientId}/challans`, body);
      if (saved.warning) toast.warning(saved.warning, { duration: 10000 }); else toast.success("Challan recorded");
      setForm(EMPTY); load(); onChange();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete challan CPIN ${c.cpin}?`)) return;
    try { await api.delete(`/clients/${clientId}/challans/${c.id}`); toast.success("Challan deleted"); load(); onChange(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-lg border border-border bg-card" data-testid="challans-card">
      <div className="px-4 py-2.5 border-b border-border font-head font-semibold text-sm">Challans paid (PMT-06)</div>
      {canEdit && (
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-border" data-testid="challan-form">
          {FIELDS.map(([k, l]) => (
            <div key={k}>
              <label className="small-label">{l}</label>
              <input data-testid={`challan-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                inputMode={["igst", "cgst", "sgst", "cess", "cpin", "period"].includes(k) ? "decimal" : undefined}
                className="mt-1 w-full rounded-md bg-background border border-input px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" />
            </div>
          ))}
          <div className="col-span-2 md:col-span-3">
            <label className="small-label">Remarks (optional)</label>
            <input data-testid="challan-remarks-input" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              className="mt-1 w-full rounded-md bg-background border border-input px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </div>
          <div className="col-span-2 md:col-span-1 flex items-end">
            <button data-testid="challan-add-button" disabled={busy || !form.cpin || !form.deposit_date} onClick={add}
              className="w-full flex items-center justify-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-3 py-2 text-xs font-medium"><Plus className="h-3.5 w-3.5" /> Record Challan</button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono" data-testid="challans-table">
          <thead className="text-muted-foreground"><tr className="[&>th]:px-2 [&>th]:py-2 [&>th]:text-left border-b border-border">
            <th>Deposit date</th><th>CPIN</th><th>CIN</th><th>Period</th><th className="text-right">IGST</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">Cess</th><th>Recorded by</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-border/40 [&>td]:px-2 [&>td]:py-1.5" data-testid={`challan-row-${c.cpin}`}>
                <td className="whitespace-nowrap">{c.deposit_date.split("-").reverse().join("-")}
                  {c.warning && <span data-testid={`challan-warning-${c.cpin}`} title={c.warning} className="ml-1 inline-flex align-middle text-amber-400"><AlertTriangle className="h-3.5 w-3.5" /></span>}
                </td><td>{c.cpin}</td><td>{c.cin || "—"}</td><td>{c.period || "—"}</td>
                {["igst", "cgst", "sgst", "cess"].map((h) => <td key={h} className="text-right">{inr(c[h])}</td>)}
                <td className="font-sans">{c.created_by_name}</td>
                <td>{canEdit && <button data-testid={`challan-delete-${c.cpin}`} onClick={() => remove(c)} className="text-rose-400 hover:text-rose-500" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={10} className="px-2 py-4 text-center text-muted-foreground font-sans">No challans recorded yet. Cash payments for filed returns are assumed until you record them.</td></tr>}
          </tbody>
        </table>
        {rows.some((c) => c.warning) && (
          <div className="px-4 py-3 space-y-1 border-t border-border" data-testid="challan-warnings">
            {rows.filter((c) => c.warning).map((c) => (
              <div key={c.id} className="flex gap-2 text-xs text-amber-400"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> CPIN {c.cpin}: {c.warning}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function LedgersPanel({ clientId }) {
  const [fy, setFy] = useState(FYS[0]);
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get(`/clients/${clientId}/ledgers?fy=${fy}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [clientId, fy, tick]);
  const download = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/ledgers/export?fy=${fy}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename\*=UTF-8''([^;]+)/);
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href; a.download = m ? decodeURIComponent(m[1]) : `${fy}_Cash_and_Credit_Ledger.xlsx`; a.click(); URL.revokeObjectURL(href);
      toast.success("Ledger downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="ledgers-panel">
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="ledger-fy-select" value={fy} onChange={(e) => setFy(e.target.value)} className="rounded-md bg-card border border-border px-3 py-1.5 text-sm font-mono">
          {FYS.map((f) => <option key={f} value={f}>FY {f}</option>)}
        </select>
        <button data-testid="ledger-download-button" onClick={download} className="flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 text-xs font-medium transition-colors">
          <Download className="h-3.5 w-3.5" /> Download Excel
        </button>
        {data && <span className="text-xs text-muted-foreground">{data.source} · {data.returns_considered} return(s). Set-off order: IGST → CGST → SGST, then CGST, then SGST.</span>}
      </div>
      {!data ? <div className="text-sm text-muted-foreground font-mono">Loading ledgers…</div> : <>
        <div className="grid gap-4 md:grid-cols-2">
          <Balance title="Credit ledger closing balance" icon={CreditCard} bal={data.closing_credit} testid="ledger-closing-credit" />
          <Balance title="Cash ledger closing balance" icon={Wallet} bal={data.closing_cash} testid="ledger-closing-cash" />
        </div>
        <LedgerTable title="Electronic Credit Ledger" rows={data.credit_ledger} testid="credit-ledger-table" />
        <LedgerTable title="Electronic Cash Ledger" rows={data.cash_ledger} testid="cash-ledger-table" />
        {data.cash_ledger.some((r) => r.assumed) && <p className="text-xs text-amber-400" data-testid="ledger-assumed-note">* Deposit assumed: no challan recorded for this payment. Record the challan below to match the GST portal.</p>}
        <ChallansCard clientId={clientId} onChange={() => setTick((t) => t + 1)} />
      </>}
    </div>
  );
}
